/**
 * Jobnotificationpanel.jsx
 * Floating notification panel — shows Legal Drafting + Brand Generator jobs.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, CheckCircle, XCircle, Loader2, Sparkles,
  ChevronDown, ChevronUp, X, Trash2, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { useJobQueue } from '../../contexts/Jobqueuecontext';

const DRAFTING_LABELS = {
  tm_examination_reply: 'Reply to Examination Report',
  tm_opposition_filing: 'Opposition Filing',
  tm_counter_statement: 'Counter Statement Reply',
  tm_rule_45:           'Rule 45',
  tm_rule_46:           'Rule 46',
  tm_rule_47:           'Rule 47',
  tm_show_cause:        'Show Cause Hearing Document',
  tm_rectification:     'Rectification Petition',
};
const labelFor = (key) => DRAFTING_LABELS[key] || key?.replace(/_/g, ' ') || 'Document';

// ─── Single job row ───────────────────────────────────────────────────────────
const JobRow = ({ job, onRemove, onCancel, onView }) => {
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [statusMsg,  setStatusMsg]  = useState(null);
  const isBrand   = job.jobType === 'brand';
  const isPending = job.status === 'generating' || job.status === 'pending';
  const isProcessing = job.status === 'processing'; // openai done, TM search running on page
  const isDone    = job.status === 'done';
  const isFailed  = job.status === 'failed';

  const handleXClick = () => {
    if (isPending) setConfirming(true);
    else onRemove(job.uid);
  };

  const handleConfirmCancel = async () => {
    setCancelling(true);
    setConfirming(false);
    await onCancel(job.uid, {
      onAlreadyDone:     () => setStatusMsg('alreadyDone'),
      onStillProcessing: () => setStatusMsg('stillProcessing'),
    });
    setCancelling(false);
  };

  if (statusMsg === 'alreadyDone') return (
    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-emerald-50 dark:bg-emerald-900/20">
      <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
        <CheckCircle className="w-3.5 h-3.5" />
        Already generated — check Generated Documents!
      </p>
    </div>
  );

  if (statusMsg === 'stillProcessing') return (
    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-amber-50 dark:bg-amber-900/20">
      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5" />
        Removed — quota will increment when AI finishes.
      </p>
    </div>
  );

  return (
    <div className="flex items-start gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
      {/* Type + status icon */}
      <div className="flex-shrink-0 mt-0.5">
        {(isPending || isProcessing) && !cancelling && (
          isBrand
            ? <Sparkles className="w-4 h-4 text-violet-400 animate-pulse" />
            : <Loader2 className="w-4 h-4 text-violet-500 animate-spin" />
        )}
        {cancelling  && <Loader2 className="w-4 h-4 text-red-400 animate-spin" />}
        {isDone      && <CheckCircle className="w-4 h-4 text-emerald-500" />}
        {isFailed    && <XCircle className="w-4 h-4 text-red-500" />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
            {isBrand ? (job.keywords ? `Brand: ${job.keywords.slice(0, 25)}` : 'Brand Names') : labelFor(job.draftingType)}
          </p>
          <span className={`text-[9px] font-bold px-1 py-0.5 rounded uppercase ${isBrand ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300' : 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300'}`}>
            {isBrand ? '✨ Brand' : '⚖️ Legal'}
          </span>
        </div>
        {job.tmNo && !isBrand && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">TM #{job.tmNo}</p>
        )}

        {confirming ? (
          <div className="mt-1.5 space-y-1.5">
            <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium leading-snug">
              ⚠️ Request already sent to AI. Removing won't stop generation — quota will still increment.
            </p>
            <div className="flex items-center gap-2">
              <button onClick={handleConfirmCancel} className="text-[10px] font-bold px-2 py-0.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors">Yes, remove</button>
              <button onClick={() => setConfirming(false)} className="text-[10px] font-bold px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md">Keep</button>
            </div>
          </div>
        ) : (
          <p className={`text-[10px] font-medium mt-0.5 ${
            cancelling   ? 'text-red-400' :
            isProcessing ? 'text-amber-500' :
            isPending    ? 'text-violet-500' :
            isDone       ? 'text-emerald-600 dark:text-emerald-400' :
                           'text-red-600 dark:text-red-400'
          }`}>
            {cancelling   ? 'Checking AI status…' :
             isProcessing ? 'Running TM search…' :
             isPending     ? 'Generating…' :
             isDone        ? (isBrand ? `${job.names?.length || 0} names ready` : 'Ready to download') :
                             (job.errorMessage || 'Generation failed')}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {isDone && (
          <button
            onClick={() => onView(job)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800 rounded-lg hover:bg-violet-100 transition-colors"
          >View <ExternalLink className="w-2.5 h-2.5" /></button>
        )}
        {!confirming && !cancelling && (
          <button
            onClick={handleXClick}
            title={isPending ? 'Cancel' : 'Dismiss'}
            className={`p-1 rounded transition-colors ${isPending ? 'text-red-400 hover:text-red-600' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}
          ><X className="w-3 h-3" /></button>
        )}
      </div>
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────
const JobNotificationPanel = () => {
  const navigate = useNavigate();
  const { jobs, removeJob, cancelJob, clearCompleted, pendingCount, completedCount, failedCount } = useJobQueue();

  const [expanded, setExpanded] = useState(false);
  const prevPendingCount = useRef(0);
  const prevJobCount     = useRef(0);

  useEffect(() => {
    const total = jobs.length;
    if (total > prevJobCount.current) setExpanded(true);
    if (pendingCount < prevPendingCount.current && prevPendingCount.current > 0) setExpanded(true);
    prevJobCount.current     = total;
    prevPendingCount.current = pendingCount;
  }, [jobs.length, pendingCount]);

  if (jobs.length === 0) return null;

const handleView = (job) => {
    if (job.jobType === 'brand') {
      const expandParam = job.usageId ? `&expand=${job.usageId}` : '';
      navigate(`/brand-generator?tab=history${expandParam}`);
    } else {
      navigate(`/legal-drafting?tab=history&draft=${job.id}&t=${Date.now()}`);
    }
    setExpanded(false);
    // Remove the job from the panel after viewing so panel disappears if empty
    // removeJob(job.uid);
  };
  
  const totalBadge  = pendingCount > 0 ? pendingCount : completedCount + failedCount;
  const badgeColor  = pendingCount > 0 ? 'bg-violet-600' : failedCount > 0 && completedCount === 0 ? 'bg-red-500' : 'bg-emerald-500';

  return (
    <div className="fixed z-[9990] flex flex-col" style={{ bottom: '5rem', right: '1.5rem', width: '340px' }}>

      {expanded && (
        <div className="mb-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-600 to-indigo-600">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-white" />
              <span className="text-sm font-bold text-white">AI Jobs</span>
              {pendingCount > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-white/20 text-white rounded-full">{pendingCount} active</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {(completedCount > 0 || failedCount > 0) && (
                <button onClick={clearCompleted} className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition-colors" title="Clear completed">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <button onClick={() => setExpanded(false)} className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Job list */}
          <div className="max-h-80 overflow-y-auto">
            {jobs.map(job => (
              <JobRow key={job.uid} job={job} onRemove={removeJob} onCancel={cancelJob} onView={handleView} />
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-3 text-[10px] font-semibold">
              {pendingCount > 0 && <span className="text-violet-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {pendingCount} generating</span>}
              {completedCount > 0 && <span className="text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {completedCount} done</span>}
              {failedCount > 0 && <span className="text-red-600 flex items-center gap-1"><XCircle className="w-3 h-3" /> {failedCount} failed</span>}
            </div>
          </div>
        </div>
      )}

      {/* Collapsed badge */}
      <button onClick={() => setExpanded(e => !e)} className="self-end flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg hover:shadow-xl transition-all">
        <div className="relative">
          <FileText className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          {pendingCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-violet-600 rounded-full flex items-center justify-center">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping absolute" />
              <span className="w-1.5 h-1.5 bg-white rounded-full relative" />
            </span>
          )}
        </div>
        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
          {pendingCount > 0 ? `${pendingCount} generating…` : `${jobs.length} job${jobs.length !== 1 ? 's' : ''}`}
        </span>
        <span className={`px-1.5 py-0.5 text-[10px] font-bold text-white rounded-full ${badgeColor}`}>{totalBadge}</span>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronUp className="w-3.5 h-3.5 text-gray-400" />}
      </button>
    </div>
  );
};

export default JobNotificationPanel;
